// ============================================================================
// Entity: CustomPages
// Key: customPages
// Kind: entity
// Generated file. Do not edit manually.
// ============================================================================

import {
	customPagesCreate,
	customPagesDelete,
	customPagesRelationsTeamsAssign,
	customPagesRelationsTeamsUnassign,
	customPagesRelationsUsersAssign,
	customPagesRelationsUsersUnassign,
	customPagesUpdate,
	getCustomPagesListQueryKey,
	getCustomPagesListQueryOptions,
	getCustomPagesRelationsTeamsAssignedQueryKey,
	getCustomPagesRelationsTeamsAssignedQueryOptions,
	getCustomPagesRelationsTeamsAvailableQueryKey,
	getCustomPagesRelationsTeamsAvailableQueryOptions,
	getCustomPagesRelationsUsersAssignedQueryKey,
	getCustomPagesRelationsUsersAssignedQueryOptions,
	getCustomPagesRelationsUsersAvailableQueryKey,
	getCustomPagesRelationsUsersAvailableQueryOptions,
	getTeamsListQueryKey,
	getUsersListQueryKey,
} from '../api/backstage'
// #region Imports
import type {
	CreateOrUpdateCustomPageRequest,
	CustomPageDto,
	PagedResultOfCustomPageDto,
	PagedResultOfTeamCustomPageResponse,
	PagedResultOfTeamSummary,
	PagedResultOfUserCustomPageDto,
	PagedResultOfUserDto,
	TeamCustomPageResponse,
	UserCustomPageDto,
} from '../api/backstage.schemas'
import type { EntityConfig, ListQueryParams } from './entity-config.types'
import {
	defineMutationEndpoint,
	defineQueryEndpoint,
} from './entity-config.types'

// #endregion

// #region Entity Endpoints
const customPagesEntityEndpoints = {
	create: defineMutationEndpoint<
		CustomPageDto,
		CreateOrUpdateCustomPageRequest,
		undefined
	>({
		key: 'customPages.create',
		method: 'POST',
		path: '/admin/custompages',
		mutationOptions: () => ({
			mutationFn: (variables: { data: CreateOrUpdateCustomPageRequest }) =>
				customPagesCreate(variables.data),
		}),
		invalidates: [(_context) => getCustomPagesListQueryKey()],
	}),
	delete: defineMutationEndpoint<void, undefined, { customPageId: number }>({
		key: 'customPages.delete',
		method: 'DELETE',
		path: '/admin/custompages/{customPageId}',
		pathParams: ['customPageId'] as const,
		mutationOptions: () => ({
			mutationFn: (variables: {
				customPageId: number | string | null | undefined
			}) => customPagesDelete(variables.customPageId),
		}),
		invalidates: [(_context) => getCustomPagesListQueryKey()],
	}),
	list: defineQueryEndpoint<
		PagedResultOfCustomPageDto,
		undefined,
		ListQueryParams
	>({
		key: 'customPages.list',
		method: 'GET',
		path: '/admin/custompages',
		queryKey: (args) => getCustomPagesListQueryKey(args?.queryParams),
		queryOptions: (args) => getCustomPagesListQueryOptions(args?.queryParams),
	}),
	update: defineMutationEndpoint<
		CustomPageDto,
		CreateOrUpdateCustomPageRequest,
		{ customPageId: number }
	>({
		key: 'customPages.update',
		method: 'PUT',
		path: '/admin/custompages/{customPageId}',
		pathParams: ['customPageId'] as const,
		mutationOptions: () => ({
			mutationFn: (variables: {
				customPageId: number | string | null | undefined
				data: CreateOrUpdateCustomPageRequest
			}) => customPagesUpdate(variables.customPageId, variables.data),
		}),
		invalidates: [(_context) => getCustomPagesListQueryKey()],
	}),
} as const
// #endregion

// #region Relations
// #region Relation: Teams
// Target Entity: teams
const customPagesTeamsRelation = {
	key: 'teams',
	targetEntity: 'teams',
	endpoints: {
		assign: defineMutationEndpoint<
			TeamCustomPageResponse[],
			(number | string)[],
			{ customPageId: number }
		>({
			key: 'customPages.relations.teams.assign',
			method: 'POST',
			path: '/admin/custompages/{customPageId}/teams',
			pathParams: ['customPageId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					customPageId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					customPagesRelationsTeamsAssign(
						variables.customPageId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getCustomPagesRelationsTeamsAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getCustomPagesRelationsTeamsAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getCustomPagesListQueryKey(),
				(_context) => getTeamsListQueryKey(),
			],
		}),
		assigned: defineQueryEndpoint<
			PagedResultOfTeamCustomPageResponse,
			{ customPageId: number },
			ListQueryParams
		>({
			key: 'customPages.relations.teams.assigned',
			method: 'GET',
			path: '/admin/custompages/{customPageId}/teams',
			pathParams: ['customPageId'] as const,
			queryKey: (args) =>
				getCustomPagesRelationsTeamsAssignedQueryKey(
					args?.pathParams?.customPageId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getCustomPagesRelationsTeamsAssignedQueryOptions(
					args?.pathParams?.customPageId,
					args?.queryParams,
				),
		}),
		available: defineQueryEndpoint<
			PagedResultOfTeamSummary,
			{ customPageId: number },
			ListQueryParams
		>({
			key: 'customPages.relations.teams.available',
			method: 'GET',
			path: '/admin/custompages/{customPageId}/assignable-teams',
			pathParams: ['customPageId'] as const,
			queryKey: (args) =>
				getCustomPagesRelationsTeamsAvailableQueryKey(
					args?.pathParams?.customPageId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getCustomPagesRelationsTeamsAvailableQueryOptions(
					args?.pathParams?.customPageId,
					args?.queryParams,
				),
		}),
		unassign: defineMutationEndpoint<
			void,
			(number | string)[],
			{ customPageId: number }
		>({
			key: 'customPages.relations.teams.unassign',
			method: 'DELETE',
			path: '/admin/custompages/{customPageId}/teams',
			pathParams: ['customPageId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					customPageId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					customPagesRelationsTeamsUnassign(
						variables.customPageId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getCustomPagesRelationsTeamsAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getCustomPagesRelationsTeamsAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getCustomPagesListQueryKey(),
				(_context) => getTeamsListQueryKey(),
			],
		}),
	},
} as const
// #endregion

// #region Relation: Users
// Target Entity: users
const customPagesUsersRelation = {
	key: 'users',
	targetEntity: 'users',
	endpoints: {
		assign: defineMutationEndpoint<
			UserCustomPageDto[],
			(number | string)[],
			{ customPageId: number }
		>({
			key: 'customPages.relations.users.assign',
			method: 'POST',
			path: '/admin/custompages/{customPageId}/users',
			pathParams: ['customPageId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					customPageId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					customPagesRelationsUsersAssign(
						variables.customPageId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getCustomPagesRelationsUsersAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getCustomPagesRelationsUsersAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getCustomPagesListQueryKey(),
				(_context) => getUsersListQueryKey(),
			],
		}),
		assigned: defineQueryEndpoint<
			PagedResultOfUserCustomPageDto,
			{ customPageId: number },
			ListQueryParams
		>({
			key: 'customPages.relations.users.assigned',
			method: 'GET',
			path: '/admin/custompages/{customPageId}/users',
			pathParams: ['customPageId'] as const,
			queryKey: (args) =>
				getCustomPagesRelationsUsersAssignedQueryKey(
					args?.pathParams?.customPageId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getCustomPagesRelationsUsersAssignedQueryOptions(
					args?.pathParams?.customPageId,
					args?.queryParams,
				),
		}),
		available: defineQueryEndpoint<
			PagedResultOfUserDto,
			{ customPageId: number },
			ListQueryParams
		>({
			key: 'customPages.relations.users.available',
			method: 'GET',
			path: '/admin/custompages/{customPageId}/assignable-users',
			pathParams: ['customPageId'] as const,
			queryKey: (args) =>
				getCustomPagesRelationsUsersAvailableQueryKey(
					args?.pathParams?.customPageId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getCustomPagesRelationsUsersAvailableQueryOptions(
					args?.pathParams?.customPageId,
					args?.queryParams,
				),
		}),
		unassign: defineMutationEndpoint<
			void,
			(number | string)[],
			{ customPageId: number }
		>({
			key: 'customPages.relations.users.unassign',
			method: 'DELETE',
			path: '/admin/custompages/{customPageId}/users',
			pathParams: ['customPageId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					customPageId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					customPagesRelationsUsersUnassign(
						variables.customPageId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getCustomPagesRelationsUsersAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getCustomPagesRelationsUsersAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getCustomPagesListQueryKey(),
				(_context) => getUsersListQueryKey(),
			],
		}),
	},
} as const
// #endregion
// #endregion

// #region Entity Config Export
export const customPagesEntityConfig = {
	key: 'customPages',
	kind: 'entity',
	entity: {
		idField: 'id',
		idParam: 'customPageId',
		routePath: '/customPages',
	},
	endpoints: customPagesEntityEndpoints,
	relations: {
		teams: customPagesTeamsRelation,
		users: customPagesUsersRelation,
	},
} as const satisfies EntityConfig<'customPages'>
// #endregion
